import { zodResolver } from "@hookform/resolvers/zod";
import { Accordion, Box, Button, Card, Group, List, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { InfoIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import { IconMapPin } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/_Input";
import { ImageCropper } from "@/components/ImageCropper";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { useGetSettingsByCompanyQuery, useUpdateSettingsMutation, useUploadLogoSettingsByCompanyMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/informacoes")({
  component: RouteComponent
});

const schema = z.object({
  googleMaps: z.url({ error: "Deve ser uma URL válida" }).or(z.string().min(1, "Deve ser uma URL válida ou código de incorporação")),
  logo: z
    .union([
      z
        .instanceof(File, { error: "Deve ser um arquivo de imagem válido" })
        .refine((file) => file.type.startsWith("image/"), { error: (params) => (params.code === "invalid_type" ? "Deve ser um arquivo de imagem válido" : undefined) }),
      z.string()
    ])
    .optional()
});
type Schema = z.infer<typeof schema>;

function RouteComponent() {
  const { colors } = useMantineTheme();
  const { companyId } = useSelectedCompany();
  const { data, isSuccess, isFetching } = useGetSettingsByCompanyQuery(
    { id: Number(companyId) }, 
    { skip: !companyId || !Number(companyId) }
  );
  const [editSettings] = useUpdateSettingsMutation();
  const [uploadLogoSettingsByCompany] = useUploadLogoSettingsByCompanyMutation();

  const {
    handleSubmit,
    watch,
    control,
    reset,
    formState: { isSubmitting, isValid }
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      googleMaps: data?.googleMaps,
      logo: undefined
    },
    mode: "all"
  });

  const googleMaps = watch("googleMaps");

  const onSubmit: SubmitHandler<Schema> = async (values) => {
    const { googleMaps, logo } = values;

    if (!isSuccess) {
      console.log("onSubmit: isSuccess é false, retornando");
      return;
    }

    const settingsData = Object.assign({}, data, { company: Number(companyId), googleMaps });
    const uploadPromise = logo instanceof File ? uploadLogoSettingsByCompany({ id: Number(companyId), logo }) : Promise.resolve();

    const promises = [editSettings(settingsData), uploadPromise] as const;
    await Promise.allSettled(promises);
  };

  // Extrair src do iframe do Google Maps se fornecido
  const getMapSrc = (embedCode: string) => {
    if (!embedCode) return "";

    // Se for um iframe completo, extrair o src
    const iframeMatch = embedCode.match(/src="([^"]+)"/);
    if (iframeMatch) {
      return iframeMatch[1];
    }

    // Se for apenas uma URL, usar diretamente
    if (embedCode.startsWith("http")) {
      return embedCode;
    }

    return "";
  };

  useEffect(() => {
    if (isSuccess) {
      reset((values) => ({
        ...values,
        logo: data.company.logo!,
        googleMaps: data.googleMaps
      }));
    }

    if (isFetching) {
      reset({
        logo: undefined,
        googleMaps: ""
      });
    }
  }, [data, isSuccess, isFetching]);

  return (
    <form onSubmit={handleSubmit(onSubmit, (errors) => alert(JSON.stringify(errors, null, 2)))}>
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Title order={2}>Informações Gerais</Title>

          <Button type="submit" variant="filled" color="blue" loading={isSubmitting || isFetching} disabled={!isSuccess || !isValid}>
            Salvar Informações
          </Button>
        </Group>

        {/* Debug de erros - renderiza apenas em ambiente de desenvolvimento */}
        {/* {process.env.NODE_ENV === "development" && Object.keys(errors).length > 0 && (
          <Card shadow="sm" p="lg" radius="md" withBorder bg="red.0">
            <Title order={5} c="red.8">
              Erros de Validação:
            </Title>
            <pre style={{ fontSize: "12px" }}>{JSON.stringify(errors, null, 2)}</pre>
          </Card>
        )} */}

        {/* Upload de Logo */}
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={4}>Logo da Empresa</Title>

            <Controller
              name="logo"
              control={control}
              render={({ field: { ...field }, fieldState: { error } }) => (
                <ImageCropper.Upload
                  {...field}
                  label="Selecionar Logo"
                  placeholder="Clique para selecionar uma imagem"
                  description="Formatos aceitos: JPG, PNG, Tamanho máximo: 5MB"
                  error={error?.message}
                  aspectRatio={1}
                  cropperHeight={300}
                  previewWidth={200}
                  previewHeight={150}
                  accept="image/*"
                />
              )}
            />
          </Stack>
        </Card>

        {/* Google Maps */}
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={4}>Localização (Google Maps)</Title>

            <Input.Text
              label="Código de Incorporação do Google Maps"
              placeholder="Cole aqui o código iframe do Google Maps ou URL direta"
              description="Vá ao Google Maps, clique em 'Compartilhar' > 'Incorporar um mapa' e cole o código aqui"
              leftSection={<IconMapPin size={16} />}
              control={control}
              name="googleMaps"
            />

            {/* Preview do mapa */}
            {googleMaps && (
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Preview do Mapa:
                </Text>

                {getMapSrc(googleMaps) && (
                  <iframe
                    src={getMapSrc(googleMaps)}
                    width="100%"
                    height="300"
                    style={{ border: 0, borderRadius: 8 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                )}

                {!getMapSrc(googleMaps) && (
                  <Box w="100%" h={300} bg="gray.1" className="flex items-center justify-center rounded-md border border-gray-300">
                    <Stack align="center" gap="xs">
                      <IconMapPin size={32} color="gray" />
                      <Text size="sm" c="dimmed" ta="center">
                        Formato de incorporação inválido
                      </Text>
                      <Text size="xs" c="dimmed" ta="center" maw={300}>
                        Certifique-se de colar o código iframe completo ou uma URL válida do Google Maps
                      </Text>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </Card>

        <Accordion>
          <Accordion.Item value="default">
            <Accordion.Control icon={<InfoIcon weight="fill" color={colors.blue[5]} />}>Como obter o código do Google Maps</Accordion.Control>

            <Accordion.Panel>
              <List type="ordered">
                <List.Item>Acesse o Google Maps e procure pelo local desejado.</List.Item>
                <List.Item>
                  <div className="flex items-center gap-1">
                    Clique no botão &quot;Compartilhar&quot;. <ShareNetworkIcon />
                  </div>
                </List.Item>
                <List.Item>Selecione &quot;Incorporar um mapa&quot;.</List.Item>
                <List.Item>Copie o código HTML fornecido.</List.Item>
                <List.Item>Cole o código completo no campo acima.</List.Item>
              </List>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </form>
  );
}
